import {useCallback, useEffect, useState} from 'react'
import {ChevronLeft, ChevronRight, Filter, ArrowLeft, Download} from 'lucide-react'
import {Heading} from '@/components/tailwind/heading'
import {Text} from '@/components/tailwind/text'
import {Badge} from '@/components/tailwind/badge'
import {Button} from '@/components/tailwind/button'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/tailwind/table'
import {Select} from '@/components/tailwind/select'
import {Input} from '@/components/tailwind/input'
import {useAdminTranslation} from "@/i18n/hooks"
import {useAuth} from '@/context/use-auth'
import {useNavigate} from 'react-router-dom'
import LoadingSpinner from "@/components/loader-spinner"
import {getAuditLogs, exportAuditLogs} from '@/services/admin-audit-log-services'
import type {AuditLogEntry} from '@/services/admin-audit-log-services'
import {formatDateTimeString} from '@/lib/utils'
import log from '@/lib/logger'

const PAGE_SIZE = 20

const ENTITY_TYPES = [
    'USER',
    'SERVICE_GROUP',
    'SERVICE',
    'PRICING_SCHEME',
    'MILEAGE_RATE',
    'MEMBERSHIP_FEE',
    'ORGANIZATION_SETTINGS',
    'ORGANIZATION_DETAILS',
    'NDA_SETTINGS',
    'MOBILITY_OPTION',
    'DISTRICT',
    'DISTRICT_STREET',
    'DELIVERY_DISTRICT',
    'DELIVERER',
    'SUBMISSION_CHANNEL',
    'RESIGNATION_REASON',
    'BILLING_UNIT_CONFIG',
    'BILLING_RUN_ITEM',
    'MDB_IMPORT',
    'STATISTICS_EXPORT',
    'EMAIL_CONFIG',
    'EMAIL',
    'EMAIL_TEMPLATE',
    'PASSWORD_POLICY',
    'PASSWORD_BLOCKLIST',
    'SOCIAL_ACCOUNT',
] as const

const EXCLUDED_ENTITY_TYPES = ['MEMBER']

function formatDetails(details: string | null, t: (key: string) => string): string {
    if (!details) return '-'
    try {
        const parsed = JSON.parse(details)
        if (parsed.changedFields) {
            const fieldLabels = (parsed.changedFields as string[]).map(
                (field: string) => t(`adminSettings.auditLog.fieldName.${field}`)
            )
            return fieldLabels.join(', ')
        }
        if (parsed.setting) {
            const settingLabel = t(`adminSettings.auditLog.settingName.${parsed.setting}`)
            const value = typeof parsed.value === 'boolean'
                ? t(`adminSettings.auditLog.booleanValue.${parsed.value ? 'yes' : 'no'}`)
                : parsed.value
            return `${settingLabel}: ${value}`
        }
        if (parsed.name && parsed.role) {
            return `${parsed.name} (${parsed.role})`
        }
        if (parsed.filename) {
            return parsed.filename
        }
        if (parsed.importStatus) {
            return t(`adminSettings.auditLog.importStatus.${parsed.importStatus}`)
        }
        if (typeof parsed.points === 'number') {
            return `${parsed.points} ${t('adminSettings.auditLog.pointsUnit')}`
        }
        return details
    } catch {
        return details
    }
}

function getActionBadgeColor(action: string): "green" | "blue" | "red" | "amber" | "zinc" {
    switch (action) {
        case 'CREATED':
            return 'green'
        case 'UPDATED':
            return 'blue'
        case 'DELETED':
        case 'SOFT_DELETED':
            return 'red'
        case 'PASSWORD_CHANGED':
        case 'PASSWORD_RESET_COMPLETED':
            return 'amber'
        case 'SET_DEFAULT':
            return 'zinc'
        case 'EXPORTED':
            return 'blue'
        case 'DONATED':
            return 'green'
        case 'DONATION_REVERSED':
            return 'amber'
        default:
            return 'zinc'
    }
}

export default function AdminAuditLogPage() {
    const {t} = useAdminTranslation()
    const {isAdmin, token} = useAuth()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [entries, setEntries] = useState<AuditLogEntry[]>([])
    const [page, setPage] = useState(0)
    const [totalElements, setTotalElements] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [entityTypeFilter, setEntityTypeFilter] = useState<string>('')
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [exporting, setExporting] = useState(false)

    useEffect(() => {
        if (!isAdmin()) {
            navigate("/dashboard")
        }
    }, [isAdmin, navigate])

    const fetchAuditLogs = useCallback(() => {
        if (!token) return
        setLoading(true)
        getAuditLogs(token, {
            entityType: entityTypeFilter || undefined,
            excludeEntityTypes: entityTypeFilter ? undefined : EXCLUDED_ENTITY_TYPES,
            page,
            size: PAGE_SIZE,
        })
            .then((result) => {
                setEntries(result.entries)
                setTotalElements(result.totalElements)
                setTotalPages(result.totalPages)
            })
            .catch((err) => {
                log.error('Failed to load audit logs:', err)
                setEntries([])
            })
            .finally(() => setLoading(false))
    }, [token, entityTypeFilter, page])

    useEffect(() => {
        fetchAuditLogs()
    }, [fetchAuditLogs])

    function handleFilterChange(newEntityType: string) {
        setEntityTypeFilter(newEntityType)
        setPage(0)
    }

    function formatLogEntry(entry: AuditLogEntry): string {
        const timestamp = formatDateTimeString(entry.createdAt)
        const user = entry.createdByName || '-'
        const entityType = t(`adminSettings.auditLog.entityType.${entry.entityType}`)
        const action = t(`adminSettings.auditLog.action.${entry.action}`)
        const name = entry.entityName || '-'
        const details = formatDetails(entry.details, t)

        let line = `[${timestamp}] ${user}\n`
        line += `  ${t('adminSettings.auditLog.columns.entityType')}: ${entityType} | ${t('adminSettings.auditLog.columns.action')}: ${action}\n`
        line += `  ${t('adminSettings.auditLog.columns.entityName')}: ${name}`
        if (details !== '-') {
            line += ` | ${t('adminSettings.auditLog.columns.details')}: ${details}`
        }
        return line
    }

    function handleDownload() {
        if (!token) return
        setExporting(true)
        exportAuditLogs(token, {
            entityType: entityTypeFilter || undefined,
            excludeEntityTypes: entityTypeFilter ? undefined : EXCLUDED_ENTITY_TYPES,
        })
            .then((result) => {
                const date = new Date().toISOString().slice(0, 10)
                const header = `=== ${t('adminSettings.auditLog.fullPage.title')} - ${date} ===\n`
                    + `${t('adminSettings.auditLog.fullPage.pagination.entries', {count: result.totalElements})}\n`

                const body = result.entries.map(formatLogEntry).join('\n\n')

                const content = header + '\n' + body + '\n'
                const blob = new Blob([content], {type: 'text/plain;charset=utf-8;'})
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `audit-log-${date}.log`
                link.click()
                URL.revokeObjectURL(url)
            })
            .catch((err) => {
                log.error('Failed to export audit logs:', err)
            })
            .finally(() => setExporting(false))
    }

    const filteredEntries = searchQuery
        ? entries.filter((entry) => {
            const query = searchQuery.toLowerCase()
            return (
                (entry.entityName?.toLowerCase().includes(query)) ||
                (entry.createdByName?.toLowerCase().includes(query)) ||
                (entry.details?.toLowerCase().includes(query))
            )
        })
        : entries

    if (!isAdmin()) return null

    return (
        <div>
            <div className="flex items-center gap-4 mb-6">
                <Button plain onClick={() => navigate('/admin/settings')}>
                    <ArrowLeft className="h-4 w-4" />
                    {t('adminSettings.auditLog.fullPage.backToSettings')}
                </Button>
            </div>

            <Heading>{t('adminSettings.auditLog.fullPage.title')}</Heading>
            <Text className="mt-1">{t('adminSettings.auditLog.fullPage.description')}</Text>

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <Filter className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
                <Select
                    value={entityTypeFilter}
                    onChange={(e) => handleFilterChange(e.target.value)}
                    className="max-w-xs"
                >
                    <option value="">{t('adminSettings.auditLog.filter.all')}</option>
                    {ENTITY_TYPES.map((type) => (
                        <option key={type} value={type}>
                            {t(`adminSettings.auditLog.entityType.${type}`)}
                        </option>
                    ))}
                </Select>
                <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('adminSettings.auditLog.fullPage.searchPlaceholder')}
                    className="max-w-xs"
                />
                <Button
                    plain
                    onClick={handleDownload}
                    disabled={exporting || totalElements === 0}
                >
                    <Download className="h-4 w-4" />
                    {t('adminSettings.auditLog.fullPage.downloadLog')}
                </Button>
            </div>

            {loading ? (
                <div className="mt-6">
                    <LoadingSpinner />
                </div>
            ) : filteredEntries.length === 0 ? (
                <Text className="mt-6 text-zinc-500 dark:text-zinc-400">
                    {t('adminSettings.auditLog.noEntries')}
                </Text>
            ) : (
                <>
                    <div className="mt-4">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('adminSettings.auditLog.columns.timestamp')}</TableHeader>
                                    <TableHeader>{t('adminSettings.auditLog.columns.user')}</TableHeader>
                                    <TableHeader>{t('adminSettings.auditLog.columns.entityType')}</TableHeader>
                                    <TableHeader>{t('adminSettings.auditLog.columns.action')}</TableHeader>
                                    <TableHeader>{t('adminSettings.auditLog.columns.entityName')}</TableHeader>
                                    <TableHeader>{t('adminSettings.auditLog.columns.details')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredEntries.map((entry) => (
                                    <TableRow key={entry.id}>
                                        <TableCell className="whitespace-nowrap text-sm">
                                            {formatDateTimeString(entry.createdAt)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {entry.createdByName || '-'}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {t(`adminSettings.auditLog.entityType.${entry.entityType}`)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge color={getActionBadgeColor(entry.action)}>
                                                {t(`adminSettings.auditLog.action.${entry.action}`)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {entry.entityName || '-'}
                                        </TableCell>
                                        <TableCell className="text-sm max-w-md truncate" title={formatDetails(entry.details, t)}>
                                            {formatDetails(entry.details, t)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="flex justify-between items-center mt-6">
                            <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('adminSettings.auditLog.fullPage.pagination.page', {current: page + 1, total: totalPages})}
                                {' '}({t('adminSettings.auditLog.fullPage.pagination.entries', {count: totalElements})})
                            </Text>
                            <div className="flex gap-2">
                                <Button
                                    plain
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 0}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {t('adminSettings.auditLog.fullPage.pagination.previous')}
                                </Button>
                                <Button
                                    plain
                                    onClick={() => setPage(page + 1)}
                                    disabled={page >= totalPages - 1}
                                >
                                    {t('adminSettings.auditLog.fullPage.pagination.next')}
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
